// LunarGrid Template CSV — bidirectional adapter between a TemplateSet and a
// human-editable CSV. Symmetric format: serialize(parse(x)) round-trips.
//
// Format (one element per row):
//
//   Element,Slot,Variant,Enabled,Skip If Too Long,Pinned Position,Match Types,Template String,Ad
//
// Element values:
//   Meta         - top-level template fields (Slot = field key)
//   Placeholder  - template-scoped placeholder (Slot = key, Template String = value)
//   Headline     - one row per (slot, variant, ad)
//   Description  - one row per (slot, variant, ad)
//   Keyword      - one row per (id, variant); Match Types is pipe-separated
//
// The "Ad" column is optional (defaults to 1). Multi-ad templates write Ad=2/3
// rows for the additional ads.

import {
  type TemplateSet,
  type TemplateSetContent,
  type HeadlineSlot,
  type DescriptionSlot,
  type KeywordItem,
  type AdDefinition,
  type KeywordMatchType,
  type TemplateVariant,
  CHARACTER_LIMITS,
} from '@/types';
import { parseCSV } from './csv';

const HEADER = [
  'Element',
  'Slot',
  'Variant',
  'Enabled',
  'Skip If Too Long',
  'Pinned Position',
  'Match Types',
  'Template String',
  'Ad',
] as const;

const META_KEYS = [
  'name',
  'language_code',
  'campaign_name_template',
  'ad_group_name_template',
  'campaign_status',
  'ad_group_status',
  'primary_grouping_field',
  'secondary_grouping_field',
  'final_url_template',
  'path1_template',
  'path2_template',
  'generate_accent_stripped_keywords',
  'use_per_ad_urls',
] as const;

type MetaKey = typeof META_KEYS[number];

export interface ParsedTemplateMeta {
  name?: string;
  language_code?: string;
}

export interface ParsedTemplateCSV {
  meta: ParsedTemplateMeta;
  // null when parse failed before any content could be assembled. Callers must check
  // `errors.length === 0` (and therefore `content != null`) before reading content.
  content: TemplateSetContent | null;
  errors: string[];
  warnings: string[];
}

// Per-ad URL collector keyed by ad index.
type PerAdUrls = Record<number, { final_url_template?: string; path1_template?: string; path2_template?: string }>;

function emptyVariant(order: number): TemplateVariant {
  return { variant_order: order, template_string: '', enabled: true };
}

function emptyHeadlineSlot(slotNumber: number): HeadlineSlot {
  return { slot_number: slotNumber, variants: [emptyVariant(1)] };
}

function emptyDescriptionSlot(slotNumber: number): DescriptionSlot {
  return { slot_number: slotNumber, variants: [emptyVariant(1)] };
}

function defaultContent(): TemplateSetContent {
  return {
    campaign_grouping_field: '',
    primary_grouping_field: undefined,
    secondary_grouping_field: undefined,
    campaign_name_template: '',
    ad_group_name_template: '',
    campaign_status: 'Enabled',
    ad_group_status: 'Enabled',
    headlines: Array.from({ length: 15 }, (_, i) => emptyHeadlineSlot(i + 1)),
    descriptions: Array.from({ length: 4 }, (_, i) => emptyDescriptionSlot(i + 1)),
    keywords: [],
    final_url_template: '',
    path1_template: '',
    path2_template: '',
    callouts: [],
    sitelinks: [],
    template_placeholders: {},
    generate_accent_stripped_keywords: true,
  };
}

function parseBool(raw: string, fallback = true): boolean {
  const v = raw.trim().toLowerCase();
  if (v === '') return fallback;
  return v === 'true' || v === '1' || v === 'yes';
}

function parseIntOrUndef(raw: string): number | undefined {
  const v = raw.trim();
  if (v === '') return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

function parseMatchTypes(raw: string): KeywordMatchType[] {
  if (!raw.trim()) return ['broad'];
  const parts = raw
    .split('|')
    .map(p => p.trim().toLowerCase())
    .filter(Boolean);
  const valid: KeywordMatchType[] = [];
  for (const p of parts) {
    if (p === 'broad' || p === 'phrase' || p === 'exact') valid.push(p);
  }
  return valid.length > 0 ? valid : ['broad'];
}

function findHeaderIndex(headers: string[], name: string): number {
  return headers.findIndex(h => h.trim().toLowerCase() === name.toLowerCase());
}

function getCol(row: string[], idx: number): string {
  if (idx < 0 || idx >= row.length) return '';
  return (row[idx] ?? '').trim();
}

// ── Parser ─────────────────────────────────────────────────────────────────

export function parseTemplateCSV(csvText: string): ParsedTemplateCSV {
  const errors: string[] = [];
  const warnings: string[] = [];
  const meta: ParsedTemplateMeta = {};
  const content = defaultContent();
  const placeholders: Record<string, string> = {};
  const perAdUrls: PerAdUrls = {};

  // Track which (slot, variant, ad) we have seen to detect duplicates.
  const seenHeadline = new Set<string>();
  const seenDescription = new Set<string>();
  const seenKeyword = new Set<string>();

  const headlinesByAd = new Map<number, Map<number, HeadlineSlot>>(); // ad -> slotNumber -> slot
  const descriptionsByAd = new Map<number, Map<number, DescriptionSlot>>();
  const keywordsById = new Map<string, KeywordItem>();
  const keywordOrder: string[] = [];

  const rows = parseCSV(csvText);
  if (rows.length === 0) {
    errors.push('CSV is empty.');
    return { meta, content, errors, warnings };
  }

  const headers = rows[0].map(h => h.trim());
  const idxElement = findHeaderIndex(headers, 'Element');
  const idxSlot = findHeaderIndex(headers, 'Slot');
  const idxVariant = findHeaderIndex(headers, 'Variant');
  const idxEnabled = findHeaderIndex(headers, 'Enabled');
  const idxSkip = findHeaderIndex(headers, 'Skip If Too Long');
  const idxPinned = findHeaderIndex(headers, 'Pinned Position');
  const idxMatch = findHeaderIndex(headers, 'Match Types');
  const idxTemplate = findHeaderIndex(headers, 'Template String');
  const idxAd = findHeaderIndex(headers, 'Ad');

  const required = [
    ['Element', idxElement],
    ['Slot', idxSlot],
    ['Template String', idxTemplate],
  ] as const;
  const missing = required.filter(([, i]) => i === -1).map(([n]) => n);
  if (missing.length > 0) {
    errors.push(`Missing required CSV columns: ${missing.join(', ')}.`);
    return { meta, content, errors, warnings };
  }

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.length === 0 || row.every(c => (c ?? '').trim() === '')) continue;

    const lineNo = r + 1; // 1-based, with header on line 1
    const element = getCol(row, idxElement).toLowerCase();
    const slot = getCol(row, idxSlot);
    const variantRaw = getCol(row, idxVariant);
    const enabledRaw = getCol(row, idxEnabled);
    const skipRaw = getCol(row, idxSkip);
    const pinnedRaw = getCol(row, idxPinned);
    const matchRaw = getCol(row, idxMatch);
    const templateString = getCol(row, idxTemplate);
    const adRaw = idxAd >= 0 ? getCol(row, idxAd) : '';
    const adIndex = parseIntOrUndef(adRaw) ?? 1;

    if (adIndex < 1 || adIndex > 3) {
      errors.push(`Line ${lineNo}: Ad must be 1, 2, or 3 (got "${adRaw}").`);
      continue;
    }

    switch (element) {
      case '': {
        // Blank Element column on a non-empty row — skip with a warning.
        warnings.push(`Line ${lineNo}: blank Element, row ignored.`);
        break;
      }

      case 'meta': {
        const key = slot.trim();
        if (!key) {
          errors.push(`Line ${lineNo}: Meta row missing Slot (field key).`);
          break;
        }
        applyMeta(key, templateString, meta, content, errors, warnings, lineNo);
        break;
      }

      case 'placeholder': {
        const key = slot.trim();
        if (!key) {
          errors.push(`Line ${lineNo}: Placeholder row missing Slot (placeholder key).`);
          break;
        }
        if (key === 'template_name') {
          warnings.push(`Line ${lineNo}: "{template_name}" is auto-injected at generation time and cannot be set as a template-scoped placeholder. Ignored.`);
          break;
        }
        if (placeholders[key] !== undefined) {
          warnings.push(`Line ${lineNo}: duplicate placeholder "${key}" — last value wins.`);
        }
        placeholders[key] = templateString;
        break;
      }

      case 'headline': {
        const slotNumber = parseIntOrUndef(slot);
        const variantOrder = parseIntOrUndef(variantRaw) ?? 1;
        if (slotNumber === undefined || slotNumber < 1 || slotNumber > 15) {
          errors.push(`Line ${lineNo}: Headline Slot must be 1-15 (got "${slot}").`);
          break;
        }
        if (variantOrder < 1) {
          errors.push(`Line ${lineNo}: Headline Variant must be >= 1 (got "${variantRaw}").`);
          break;
        }
        const dupKey = `${adIndex}|${slotNumber}|${variantOrder}`;
        if (seenHeadline.has(dupKey)) {
          errors.push(`Line ${lineNo}: duplicate Headline ad=${adIndex} slot=${slotNumber} variant=${variantOrder}.`);
          break;
        }
        seenHeadline.add(dupKey);

        if (templateString.length > CHARACTER_LIMITS.headline && !templateString.includes('{')) {
          warnings.push(`Line ${lineNo}: Headline ${slotNumber} V${variantOrder} is ${templateString.length} chars (over ${CHARACTER_LIMITS.headline}) and contains no placeholder.`);
        }

        let perAd = headlinesByAd.get(adIndex);
        if (!perAd) {
          perAd = new Map();
          headlinesByAd.set(adIndex, perAd);
        }
        let slotObj = perAd.get(slotNumber);
        if (!slotObj) {
          slotObj = { slot_number: slotNumber, variants: [] };
          perAd.set(slotNumber, slotObj);
        }

        slotObj.variants.push({
          variant_order: variantOrder,
          template_string: templateString,
          enabled: parseBool(enabledRaw, true),
        });

        // Slot-level flags: take from the row that sets them. Last-write-wins
        // across variants (typical authoring puts them on the V1 row).
        if (skipRaw !== '') slotObj.skip_if_too_long = parseBool(skipRaw, false);
        const pinned = parseIntOrUndef(pinnedRaw);
        if (pinned !== undefined) {
          if (pinned < 1 || pinned > 3) {
            errors.push(`Line ${lineNo}: Headline Pinned Position must be 1, 2, or 3 (got "${pinnedRaw}").`);
          } else {
            slotObj.pinned_position = pinned as 1 | 2 | 3;
          }
        }
        break;
      }

      case 'description': {
        const slotNumber = parseIntOrUndef(slot);
        const variantOrder = parseIntOrUndef(variantRaw) ?? 1;
        if (slotNumber === undefined || slotNumber < 1 || slotNumber > 4) {
          errors.push(`Line ${lineNo}: Description Slot must be 1-4 (got "${slot}").`);
          break;
        }
        if (variantOrder < 1) {
          errors.push(`Line ${lineNo}: Description Variant must be >= 1 (got "${variantRaw}").`);
          break;
        }
        const dupKey = `${adIndex}|${slotNumber}|${variantOrder}`;
        if (seenDescription.has(dupKey)) {
          errors.push(`Line ${lineNo}: duplicate Description ad=${adIndex} slot=${slotNumber} variant=${variantOrder}.`);
          break;
        }
        seenDescription.add(dupKey);

        if (templateString.length > CHARACTER_LIMITS.description && !templateString.includes('{')) {
          warnings.push(`Line ${lineNo}: Description ${slotNumber} V${variantOrder} is ${templateString.length} chars (over ${CHARACTER_LIMITS.description}) and contains no placeholder.`);
        }

        let perAd = descriptionsByAd.get(adIndex);
        if (!perAd) {
          perAd = new Map();
          descriptionsByAd.set(adIndex, perAd);
        }
        let slotObj = perAd.get(slotNumber);
        if (!slotObj) {
          slotObj = { slot_number: slotNumber, variants: [] };
          perAd.set(slotNumber, slotObj);
        }

        slotObj.variants.push({
          variant_order: variantOrder,
          template_string: templateString,
          enabled: parseBool(enabledRaw, true),
        });

        if (skipRaw !== '') slotObj.skip_if_too_long = parseBool(skipRaw, false);
        const pinned = parseIntOrUndef(pinnedRaw);
        if (pinned !== undefined) {
          if (pinned < 1 || pinned > 2) {
            errors.push(`Line ${lineNo}: Description Pinned Position must be 1 or 2 (got "${pinnedRaw}").`);
          } else {
            slotObj.pinned_position = pinned as 1 | 2;
          }
        }
        break;
      }

      case 'keyword': {
        const id = slot.trim();
        if (!id) {
          errors.push(`Line ${lineNo}: Keyword row missing Slot (keyword id).`);
          break;
        }
        const variantOrder = parseIntOrUndef(variantRaw) ?? 1;
        if (variantOrder < 1) {
          errors.push(`Line ${lineNo}: Keyword Variant must be >= 1 (got "${variantRaw}").`);
          break;
        }
        const dupKey = `${id}|${variantOrder}`;
        if (seenKeyword.has(dupKey)) {
          errors.push(`Line ${lineNo}: duplicate Keyword id=${id} variant=${variantOrder}.`);
          break;
        }
        seenKeyword.add(dupKey);

        if (templateString.length > CHARACTER_LIMITS.keyword && !templateString.includes('{')) {
          warnings.push(`Line ${lineNo}: Keyword ${id} V${variantOrder} is ${templateString.length} chars (over ${CHARACTER_LIMITS.keyword}) and contains no placeholder.`);
        }

        let kw = keywordsById.get(id);
        if (!kw) {
          kw = { id, match_types: parseMatchTypes(matchRaw), variants: [] };
          keywordsById.set(id, kw);
          keywordOrder.push(id);
        } else if (matchRaw) {
          // First non-empty Match Types wins; warn on later conflicts.
          const newMatch = parseMatchTypes(matchRaw);
          const same = newMatch.length === kw.match_types.length && newMatch.every(m => kw!.match_types.includes(m));
          if (!same) {
            warnings.push(`Line ${lineNo}: Match Types for keyword "${id}" differ from earlier row — first occurrence kept.`);
          }
        }
        kw.variants.push({
          variant_order: variantOrder,
          template_string: templateString,
          enabled: parseBool(enabledRaw, true),
        });
        break;
      }

      case 'peradurl':
      case 'per_ad_url': {
        const key = slot.trim().toLowerCase();
        const bucket = perAdUrls[adIndex] ?? {};
        if (key === 'final_url_template') bucket.final_url_template = templateString;
        else if (key === 'path1_template') bucket.path1_template = templateString;
        else if (key === 'path2_template') bucket.path2_template = templateString;
        else {
          errors.push(`Line ${lineNo}: PerAdUrl Slot must be final_url_template, path1_template, or path2_template (got "${slot}").`);
          break;
        }
        perAdUrls[adIndex] = bucket;
        break;
      }

      default: {
        warnings.push(`Line ${lineNo}: unknown Element "${element}", row ignored.`);
        break;
      }
    }
  }

  // ── Materialise headlines/descriptions into 15/4 padded slots ──
  const ad1Headlines = padHeadlines(headlinesByAd.get(1) ?? new Map());
  const ad1Descriptions = padDescriptions(descriptionsByAd.get(1) ?? new Map());
  content.headlines = ad1Headlines;
  content.descriptions = ad1Descriptions;

  const adIndices = Array.from(new Set([
    ...headlinesByAd.keys(),
    ...descriptionsByAd.keys(),
    ...Object.keys(perAdUrls).map(k => parseInt(k, 10)),
  ])).filter(n => Number.isFinite(n)).sort((a, b) => a - b);

  if (adIndices.length > 1 || adIndices[0] === 2 || adIndices[0] === 3) {
    const ads: AdDefinition[] = [];
    for (const adIdx of [1, 2, 3]) {
      const hMap = headlinesByAd.get(adIdx);
      const dMap = descriptionsByAd.get(adIdx);
      const urls = perAdUrls[adIdx];
      if (!hMap && !dMap && !urls) continue;
      ads.push({
        ad_index: adIdx,
        headlines: padHeadlines(hMap ?? new Map()),
        descriptions: padDescriptions(dMap ?? new Map()),
        ...(urls?.final_url_template !== undefined ? { final_url_template: urls.final_url_template } : {}),
        ...(urls?.path1_template !== undefined ? { path1_template: urls.path1_template } : {}),
        ...(urls?.path2_template !== undefined ? { path2_template: urls.path2_template } : {}),
      });
    }
    if (ads.length > 0 && !ads.some(a => a.ad_index === 1)) {
      // Ad 1 is required; legacy fields default to empty.
      ads.unshift({ ad_index: 1, headlines: ad1Headlines, descriptions: ad1Descriptions });
    }
    content.ads = ads;
    if (Object.keys(perAdUrls).length > 0) content.use_per_ad_urls = true;
  }

  // ── Sort and seal headline/description variants ──
  for (const slot of content.headlines) sortVariants(slot.variants);
  for (const slot of content.descriptions) sortVariants(slot.variants);
  if (content.ads) {
    for (const ad of content.ads) {
      for (const slot of ad.headlines) sortVariants(slot.variants);
      for (const slot of ad.descriptions) sortVariants(slot.variants);
    }
  }

  // ── Keywords ──
  const keywords: KeywordItem[] = keywordOrder.map(id => {
    const kw = keywordsById.get(id)!;
    sortVariants(kw.variants);
    return kw;
  });
  content.keywords = keywords;

  // ── Placeholders ──
  if (Object.keys(placeholders).length > 0) {
    content.template_placeholders = placeholders;
  }

  // ── Final completeness warnings ──
  if (!content.campaign_name_template) {
    warnings.push('No Meta:campaign_name_template set — generation will produce an empty Campaign column.');
  }
  if (!content.ad_group_name_template) {
    warnings.push('No Meta:ad_group_name_template set — generation will produce an empty Ad Group column.');
  }
  if (!content.headlines.some(s => s.variants.some(v => v.enabled && v.template_string.trim() !== ''))) {
    warnings.push('No enabled, non-empty headline variants found.');
  }
  if (!content.descriptions.some(s => s.variants.some(v => v.enabled && v.template_string.trim() !== ''))) {
    warnings.push('No enabled, non-empty description variants found.');
  }
  if (keywords.length === 0) {
    warnings.push('No keywords defined.');
  }

  return { meta, content, errors, warnings };
}

function sortVariants(variants: TemplateVariant[]): void {
  variants.sort((a, b) => a.variant_order - b.variant_order);
}

function padHeadlines(byNumber: Map<number, HeadlineSlot>): HeadlineSlot[] {
  return Array.from({ length: 15 }, (_, i) => {
    const n = i + 1;
    const existing = byNumber.get(n);
    if (existing && existing.variants.length > 0) return existing;
    return emptyHeadlineSlot(n);
  });
}

function padDescriptions(byNumber: Map<number, DescriptionSlot>): DescriptionSlot[] {
  return Array.from({ length: 4 }, (_, i) => {
    const n = i + 1;
    const existing = byNumber.get(n);
    if (existing && existing.variants.length > 0) return existing;
    return emptyDescriptionSlot(n);
  });
}

function applyMeta(
  key: string,
  value: string,
  meta: ParsedTemplateMeta,
  content: TemplateSetContent,
  errors: string[],
  warnings: string[],
  lineNo: number,
): void {
  const k = key.trim() as MetaKey;
  switch (k) {
    case 'name':
      meta.name = value;
      break;
    case 'language_code':
      meta.language_code = value || undefined;
      break;
    case 'campaign_name_template':
      content.campaign_name_template = value;
      break;
    case 'ad_group_name_template':
      content.ad_group_name_template = value;
      break;
    case 'campaign_status':
      if (value === 'Enabled' || value === 'Paused') content.campaign_status = value;
      else errors.push(`Line ${lineNo}: campaign_status must be "Enabled" or "Paused" (got "${value}").`);
      break;
    case 'ad_group_status':
      if (value === 'Enabled' || value === 'Paused') content.ad_group_status = value;
      else errors.push(`Line ${lineNo}: ad_group_status must be "Enabled" or "Paused" (got "${value}").`);
      break;
    case 'primary_grouping_field':
      content.primary_grouping_field = value || undefined;
      content.campaign_grouping_field = value;
      break;
    case 'secondary_grouping_field':
      content.secondary_grouping_field = value || undefined;
      break;
    case 'final_url_template':
      content.final_url_template = value;
      break;
    case 'path1_template':
      if (value.length > CHARACTER_LIMITS.display_path && !value.includes('{')) {
        warnings.push(`Line ${lineNo}: path1_template is ${value.length} chars (over ${CHARACTER_LIMITS.display_path}).`);
      }
      content.path1_template = value;
      break;
    case 'path2_template':
      if (value.length > CHARACTER_LIMITS.display_path && !value.includes('{')) {
        warnings.push(`Line ${lineNo}: path2_template is ${value.length} chars (over ${CHARACTER_LIMITS.display_path}).`);
      }
      content.path2_template = value;
      break;
    case 'generate_accent_stripped_keywords':
      content.generate_accent_stripped_keywords = parseBool(value, true);
      break;
    case 'use_per_ad_urls':
      content.use_per_ad_urls = parseBool(value, false);
      break;
    default:
      warnings.push(`Line ${lineNo}: unknown Meta key "${key}", ignored.`);
  }
}

// ── Serializer ─────────────────────────────────────────────────────────────

export function serializeTemplateCSV(template: TemplateSet): string {
  const c = template.template_content;
  const rows: string[][] = [];
  rows.push([...HEADER]);

  const meta = (k: string, v: string | undefined | null) => {
    rows.push(['Meta', k, '', '', '', '', '', v ?? '', '']);
  };

  meta('name', template.name);
  meta('language_code', template.language_code);
  meta('campaign_name_template', c.campaign_name_template);
  meta('ad_group_name_template', c.ad_group_name_template);
  meta('campaign_status', c.campaign_status);
  meta('ad_group_status', c.ad_group_status);
  meta('primary_grouping_field', c.primary_grouping_field ?? c.campaign_grouping_field ?? '');
  meta('secondary_grouping_field', c.secondary_grouping_field ?? '');
  meta('final_url_template', c.final_url_template);
  meta('path1_template', c.path1_template);
  meta('path2_template', c.path2_template);
  meta('generate_accent_stripped_keywords', String(!!c.generate_accent_stripped_keywords));
  if (c.use_per_ad_urls) meta('use_per_ad_urls', 'true');

  // Placeholders
  const phs = c.template_placeholders ?? {};
  for (const [key, value] of Object.entries(phs)) {
    rows.push(['Placeholder', key, '', '', '', '', '', value, '']);
  }

  // Helper to flush a slot's variants into rows.
  const writeHeadlineSlot = (slot: HeadlineSlot, adIndex: number) => {
    const variants = [...slot.variants].sort((a, b) => a.variant_order - b.variant_order);
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const isFirst = i === 0;
      rows.push([
        'Headline',
        String(slot.slot_number),
        String(v.variant_order),
        v.enabled ? 'true' : 'false',
        isFirst && slot.skip_if_too_long ? 'true' : '',
        isFirst && slot.pinned_position ? String(slot.pinned_position) : '',
        '',
        v.template_string,
        adIndex === 1 ? '' : String(adIndex),
      ]);
    }
  };

  const writeDescriptionSlot = (slot: DescriptionSlot, adIndex: number) => {
    const variants = [...slot.variants].sort((a, b) => a.variant_order - b.variant_order);
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const isFirst = i === 0;
      rows.push([
        'Description',
        String(slot.slot_number),
        String(v.variant_order),
        v.enabled ? 'true' : 'false',
        isFirst && slot.skip_if_too_long ? 'true' : '',
        isFirst && slot.pinned_position ? String(slot.pinned_position) : '',
        '',
        v.template_string,
        adIndex === 1 ? '' : String(adIndex),
      ]);
    }
  };

  // Multi-ad: write each ad's headlines/descriptions; emit per-ad URL rows last.
  if (c.ads && c.ads.length > 0) {
    for (const ad of c.ads) {
      for (const slot of ad.headlines) writeHeadlineSlot(slot, ad.ad_index);
      for (const slot of ad.descriptions) writeDescriptionSlot(slot, ad.ad_index);
    }
  } else {
    for (const slot of c.headlines) writeHeadlineSlot(slot, 1);
    for (const slot of c.descriptions) writeDescriptionSlot(slot, 1);
  }

  // Per-ad URL overrides (only meaningful when use_per_ad_urls)
  if (c.use_per_ad_urls && c.ads) {
    for (const ad of c.ads) {
      const writeUrl = (key: 'final_url_template' | 'path1_template' | 'path2_template') => {
        const val = ad[key];
        if (val !== undefined && val !== null) {
          rows.push(['PerAdUrl', key, '', '', '', '', '', val, String(ad.ad_index)]);
        }
      };
      writeUrl('final_url_template');
      writeUrl('path1_template');
      writeUrl('path2_template');
    }
  }

  // Keywords
  for (const kw of c.keywords) {
    const variants = [...kw.variants].sort((a, b) => a.variant_order - b.variant_order);
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      rows.push([
        'Keyword',
        kw.id,
        String(v.variant_order),
        v.enabled ? 'true' : 'false',
        '',
        '',
        i === 0 ? kw.match_types.join('|') : '',
        v.template_string,
        '',
      ]);
    }
  }

  return rows.map(rowToCSVLine).join('\n');
}

function rowToCSVLine(row: string[]): string {
  return row.map(escapeField).join(',');
}

function escapeField(field: string): string {
  if (field == null) return '';
  if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

// ── Example scaffold ───────────────────────────────────────────────────────

export function buildExampleTemplateCSV(): string {
  const lines: string[] = [];
  lines.push(HEADER.join(','));

  // Meta
  lines.push('Meta,name,,,,,,Example Care Home Template,');
  lines.push('Meta,language_code,,,,,,fr,');
  lines.push('Meta,campaign_name_template,,,,,,FR | CITY | {nom_standard} | GEO,');
  lines.push('Meta,ad_group_name_template,,,,,,{nom_standard} {home_type},');
  lines.push('Meta,campaign_status,,,,,,Enabled,');
  lines.push('Meta,ad_group_status,,,,,,Enabled,');
  lines.push('Meta,primary_grouping_field,,,,,,nom_standard,');
  lines.push('Meta,secondary_grouping_field,,,,,,,');
  lines.push('Meta,final_url_template,,,,,,https://example.com/{nom_slug},');
  lines.push('Meta,path1_template,,,,,,{nom_slug},');
  lines.push('Meta,path2_template,,,,,,,');
  lines.push('Meta,generate_accent_stripped_keywords,,,,,,true,');

  // Placeholder
  lines.push('Placeholder,home_type,,,,,,Maison de retraite,');

  // Headlines (slot 1 with two variants, slot 2 single)
  lines.push('Headline,1,1,true,false,1,,Top {home_type} à {nom_standard},');
  lines.push('Headline,1,2,true,,,,{home_type} à {nom_standard},');
  lines.push('Headline,2,1,true,,,,Aide pour trouver une place,');
  lines.push('Headline,3,1,true,,,,Service gratuit et rapide,');

  // Descriptions
  lines.push('Description,1,1,true,,,,Trouvez un {home_type} à {nom_standard}. Gratuit et sans engagement.,');
  lines.push('Description,2,1,true,,,,Notre équipe vous guide vers le meilleur {home_type} disponible près de {nom_standard}.,');

  // Keywords
  lines.push('Keyword,kw_main,1,true,,,broad|phrase,{home_type} {nom_standard},');
  lines.push('Keyword,kw_pricing,1,true,,,broad,prix {home_type} {nom_standard},');

  return lines.join('\n');
}

// ── Validation summary helpers (for the import preview) ────────────────────

export interface TemplateCSVSummary {
  headlineSlotsFilled: number;
  descriptionSlotsFilled: number;
  keywordCount: number;
  placeholderCount: number;
  adCount: number;
}

export function summarizeParsedContent(parsed: ParsedTemplateCSV): TemplateCSVSummary {
  const c = parsed.content;
  if (!c) {
    return { headlineSlotsFilled: 0, descriptionSlotsFilled: 0, keywordCount: 0, placeholderCount: 0, adCount: 0 };
  }
  const headlineSlotsFilled = c.headlines.filter(s => s.variants.some(v => v.enabled && v.template_string.trim() !== '')).length;
  const descriptionSlotsFilled = c.descriptions.filter(s => s.variants.some(v => v.enabled && v.template_string.trim() !== '')).length;
  return {
    headlineSlotsFilled,
    descriptionSlotsFilled,
    keywordCount: c.keywords.length,
    placeholderCount: Object.keys(c.template_placeholders ?? {}).length,
    adCount: c.ads?.length ?? 1,
  };
}
